"use client";

import { useQuery } from "@tanstack/react-query";

const BASE = process.env.NEXT_PUBLIC_BASE_URL ?? "";

/**
 * Whether the ready-made catalogue is offered to customers.
 *
 * This was a build-time environment variable, which meant opening the shop
 * required editing a GitHub variable and waiting for a Docker build — a poor
 * shape for a decision someone wants to make, or undo, in a hurry. It is now
 * a row in the `setting` table, thrown from admin, and takes effect within
 * about a minute.
 *
 * It hides the entrances — the wall-art hub, the menu entry, the products
 * tile — and puts noindex on the pages. It is NOT what stops anyone buying.
 * The API is public, so the guarantee is `active = false` on the artworks,
 * which ArtworkOrderService.resolve() enforces. Presentation here,
 * enforcement there; a hidden link is not access control.
 *
 * While the setting is loading this returns FALSE, i.e. hidden. Defaulting
 * the other way would flash the catalogue onto the page for a moment on every
 * cold load, which for a shop that has not opened yet is the whole thing
 * going wrong for a second at a time.
 */
export function useArtCatalogueEnabled(): boolean {
  const { data } = useQuery(publicSettingsQuery);
  return data?.artCatalogueEnabled === true;
}

/**
 * The public settings read, shared by every switch hook so they are one
 * request under one cache key (see also gift-cards-enabled.ts).
 */
export const publicSettingsQuery = {
  queryKey: ["settings", "public"],
  queryFn: async () => {
    const res = await fetch(`${BASE}/setting/public`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as {
      artCatalogueEnabled?: boolean;
      giftCardsEnabled?: boolean;
      giftCardsRedeemable?: boolean;
      mapsEnabled?: boolean;
      cashbackEnabled?: boolean;
      bundlesEnabled?: boolean;
      freeEnlargementEnabled?: boolean;
      partyCameraEnabled?: boolean;
    };
  },
  // Changes a few times a year, read on every page. Long enough to be free,
  // short enough that an operator sees their switch take effect.
  staleTime: 60_000,
  refetchOnWindowFocus: false,
  // If the settings call fails we must not guess "open".
  retry: 1,
};
